/**
 * Word Management Types
 *
 * TypeScript interfaces for word management within word lists
 */

// Pronunciation data structure
export interface PronunciationData {
  ipa: string | null;
  audioUrl: string | null;
}

// Frontend Word type (camelCase)
export interface Word {
  cardId: string;
  word: string;
  languageCode: string;
  imageUrl: string | null;
  exampleSentence: string | null;
  exampleTranslations: Record<string, string> | null;
  translations: Record<string, string> | null;
  pronunciations: Record<string, PronunciationData> | null;
  senseRank: number | null;
  createdAt: string;
  updatedAt: string;
}

// Word with order context (for list view)
export interface WordInList extends Word {
  order: number;
}

// Paginated words response
export interface WordsInListResponse {
  items: WordInList[];
  total: number;
  page: number;
  pageSize: number;
}

// Word exists check response
export interface WordExistsResponse {
  exists: boolean;
  word: Word | null;
}

// Request types (snake_case for API)
export interface AddWordToListRequest {
  card_id?: string;
  word: string;
  language_code: string;
  image_url?: string; // Optional when adding existing word
  example_sentence?: string; // Optional when adding existing word
  example_translations?: Record<string, string>;
  translations?: Record<string, string>;
  pronunciations?: Record<string, { ipa?: string; audio_url?: string }>;
  sense_rank?: number | null;
  order?: number;
}

export interface WordUpdateRequest {
  image_url?: string;
  example_sentence?: string;
  example_translations?: Record<string, string>;
  translations?: Record<string, string>;
  pronunciations?: Record<string, { ipa?: string; audio_url?: string }>;
  sense_rank?: number | null;
}

export interface WordOrderItem {
  card_id: string;
  word: string;
  language_code: string;
  order: number;
}

export interface ReorderWordsRequest {
  word_orders: WordOrderItem[];
}

// API response types (snake_case from backend)
export interface PronunciationDataApiResponse {
  ipa: string | null;
  audio_url: string | null;
}

export interface WordApiResponse {
  card_id: string;
  word: string;
  language_code: string;
  image_url: string | null;
  example_sentence: string | null;
  example_translations: Record<string, string> | null;
  translations: Record<string, string> | null;
  pronunciations: Record<string, PronunciationDataApiResponse> | null;
  sense_rank: number | null;
  created_at: string;
  updated_at: string;
}

export interface WordInListApiResponse extends WordApiResponse {
  order: number;
}

export interface WordsInListApiResponse {
  items: WordInListApiResponse[];
  total: number;
  page: number;
  page_size: number;
}

export interface WordExistsApiResponse {
  exists: boolean;
  word: WordApiResponse | null;
}

/**
 * Convert API pronunciation data to frontend type
 */
function mapPronunciationFromApi(
  api: Record<string, PronunciationDataApiResponse> | null
): Record<string, PronunciationData> | null {
  if (!api) return null;
  const result: Record<string, PronunciationData> = {};
  for (const [locale, data] of Object.entries(api)) {
    result[locale] = {
      ipa: data.ipa,
      audioUrl: data.audio_url,
    };
  }
  return result;
}

/**
 * Convert API response to frontend Word type
 */
export function mapWordFromApi(api: WordApiResponse): Word {
  return {
    cardId: api.card_id,
    word: api.word,
    languageCode: api.language_code,
    imageUrl: api.image_url,
    exampleSentence: api.example_sentence,
    exampleTranslations: api.example_translations,
    translations: api.translations,
    pronunciations: mapPronunciationFromApi(api.pronunciations),
    senseRank: api.sense_rank,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

/**
 * Convert API response to frontend WordInList type
 */
export function mapWordInListFromApi(api: WordInListApiResponse): WordInList {
  return {
    ...mapWordFromApi(api),
    order: api.order,
  };
}

/**
 * Convert frontend pronunciation data to API format
 */
export function mapPronunciationToApi(
  data: Record<string, PronunciationData> | null
): Record<string, { ipa?: string; audio_url?: string }> | undefined {
  if (!data) return undefined;
  const result: Record<string, { ipa?: string; audio_url?: string }> = {};
  for (const [locale, pronunciation] of Object.entries(data)) {
    result[locale] = {
      ipa: pronunciation.ipa || undefined,
      audio_url: pronunciation.audioUrl || undefined,
    };
  }
  return result;
}

// =============================================================================
// AI Generation Types
// =============================================================================

// Request types (snake_case for API)
export interface GenerateExampleSentenceRequest {
  word: string;
  language_code: string;
}

export interface GenerateTranslationsRequest {
  word: string;
  language_code: string;
  target_languages: string[];
}

export interface GenerateExampleTranslationsRequest {
  example_sentence: string;
  source_language: string;
  target_languages: string[];
}

export interface GenerateIPARequest {
  word: string;
  locale: string;
}

export interface GeneratePronunciationAudioRequest {
  card_id?: string;
  word: string;
  locale: string;
}

export interface GenerateImageRequest {
  card_id?: string;
  word: string;
  language_code: string;
}

// Response types (snake_case from backend)
export interface GenerateExampleSentenceResponse {
  example_sentence: string;
}

export interface GenerateTranslationsResponse {
  translations: Record<string, string>;
}

export interface GenerateExampleTranslationsResponse {
  translations: Record<string, string>;
}

export interface GenerateIPAResponse {
  ipa: string;
}

export interface GeneratePronunciationAudioResponse {
  audio_url: string;
  duration_seconds: number;
}

// Bulk pronunciation generation (all configured locales)
export interface GenerateAllPronunciationsRequest {
  card_id?: string;
  word: string;
  language_code: string;
  generate_ipa?: boolean;
}

export interface PronunciationResultItem {
  locale: string;
  locale_name: string;
  ipa: string | null;
  audio_url: string;
  duration_seconds: number;
}

export interface GenerateAllPronunciationsResponse {
  pronunciations: PronunciationResultItem[];
  total_generated: number;
}

// Frontend-friendly result type for bulk pronunciation
export interface AllPronunciationsResult {
  pronunciations: Array<{
    locale: string;
    localeName: string;
    ipa: string | null;
    audioUrl: string;
    durationSeconds: number;
  }>;
  totalGenerated: number;
}

export interface GenerateImageResponse {
  image_url: string;
}

// Combined word metadata generation (single LLM request)
export interface GenerateWordMetadataRequest {
  card_id?: string;
  word: string;
  language_code: string;
}

export interface GenerateWordMetadataResponse {
  example_sentence: string;
  word_translations: Record<string, string>;
  example_translations: Record<string, string>;
}

// Frontend-friendly result type
export interface WordMetadataResult {
  exampleSentence: string;
  wordTranslations: Record<string, string>;
  exampleTranslations: Record<string, string>;
}

// =============================================================================
// All Words Types (for admin word bank view)
// =============================================================================

// Word with coverage flags for all words list view
export interface WordWithCoverage extends Word {
  hasImage: boolean;
  hasTranslations: boolean;
  hasPronunciations: boolean;
  hasExample: boolean;
}

// Paginated all words response
export interface AllWordsResponse {
  items: WordWithCoverage[];
  total: number;
  page: number;
  pageSize: number;
}

// API response types (snake_case from backend)
export interface WordWithCoverageApiResponse extends WordApiResponse {
  has_image: boolean;
  has_translations: boolean;
  has_pronunciations: boolean;
  has_example: boolean;
}

export interface AllWordsApiResponse {
  items: WordWithCoverageApiResponse[];
  total: number;
  page: number;
  page_size: number;
}

/**
 * Convert API response to frontend WordWithCoverage type
 */
export function mapWordWithCoverageFromApi(api: WordWithCoverageApiResponse): WordWithCoverage {
  return {
    ...mapWordFromApi(api),
    hasImage: api.has_image,
    hasTranslations: api.has_translations,
    hasPronunciations: api.has_pronunciations,
    hasExample: api.has_example,
  };
}
